import { Children, isValidElement, type ReactNode, useCallback, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import Group from 'components/utils/Group'
import BodyText from 'LuxKit/Typography/BodyText'
import styled from 'styled-components'
import { rem } from 'polished'
import useResizeObserver from 'hooks/useResizeObserver'
import useIsomorphicLayoutEffect from 'hooks/useIsomorphicLayoutEffect'
import { getFittingGrowthInclusionCount } from 'lib/offer/growthOfferTile'

const OverflowSpace = styled(BodyText)`
  position: relative;
  flex: 1;
  min-height: calc(1lh + ${rem(4)});
`

const OverflowContent = styled(Group)`
  position: absolute;
  inset: 0 0 auto;

  > .concealed {
    position: absolute;
    top: 0;
    width: 100%;
    visibility: hidden;
    pointer-events: none;
  }
`

const MoreCount = styled(BodyText)`
  padding-top: ${rem(4)};
`

interface Props {
  children: ReactNode
  heading?: ReactNode
  additionalInclusionCount?: number
}

/**
 * Three rows establish the card's intrinsic height. Extra rows are positioned in
 * the space allocated by the equal-height carousel, so revealing them cannot make
 * the carousel taller and trigger an expansion loop. Hidden rows stay measurable
 * but inert, keeping their modal actions out of keyboard and screen-reader access.
 * The exclusive heading is separate and never consumes an inclusion slot.
 */
function GrowthInclusionList({ children, heading, additionalInclusionCount = 0 }: Props) {
  const intl = useIntl()
  const items = useMemo(() => Children.toArray(children), [children])
  const [overflowElement, setOverflowElement] = useState<HTMLDivElement | null>(null)
  const [contentElement, setContentElement] = useState<HTMLDivElement | null>(null)
  const [extraVisibleCount, setExtraVisibleCount] = useState(0)
  const extraItems = items.slice(3)

  const measure = useCallback(() => {
    const space = overflowElement
    const content = contentElement
    if (!space || !content) return
    const rowHeights = Array.from(content.children)
      .filter(child => child.hasAttribute('data-inclusion-row'))
      .map(child => child.getBoundingClientRect().height)
    const gap = parseFloat(getComputedStyle(content).rowGap) || 0
    const lineHeight = parseFloat(getComputedStyle(space).lineHeight) || 0
    if (!lineHeight || rowHeights.some(height => !height)) return
    setExtraVisibleCount(getFittingGrowthInclusionCount(space.clientHeight, rowHeights, lineHeight, gap, additionalInclusionCount))
  }, [additionalInclusionCount, contentElement, overflowElement])

  useResizeObserver(overflowElement, measure)
  useResizeObserver(contentElement, measure)
  useIsomorphicLayoutEffect(measure, [measure, children])

  if (!items.length && !heading) return null
  const remainingCount = additionalInclusionCount + extraItems.length - Math.min(extraVisibleCount, extraItems.length)

  return <Group direction="vertical" gap={4} grow>
    {heading}
    <Group direction="vertical" gap={4}>{items.slice(0, 3)}</Group>
    {(extraItems.length > 0 || additionalInclusionCount > 0) && <OverflowSpace ref={setOverflowElement} variant="small">
      <OverflowContent ref={setContentElement} direction="vertical" gap={4}>
        {extraItems.map((item, index) => <div
          key={isValidElement(item) ? item.key : index}
          data-inclusion-row
          className={index >= extraVisibleCount ? 'concealed' : undefined}
          inert={index >= extraVisibleCount}
          aria-hidden={index >= extraVisibleCount || undefined}
        >{item}
        </div>)}
        {remainingCount > 0 && <MoreCount variant="small" weight="bold">
          {intl.formatMessage({
            id: '-_Y7PlvvzQaDdQJ6J66z',
            defaultMessage: '+{count} more',
            description: 'Text indicating additional inclusions count on offer card',
          }, { count: remainingCount })}
        </MoreCount>}
      </OverflowContent>
    </OverflowSpace>}
  </Group>
}

export default GrowthInclusionList
